import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { TKeyInitiative } from 'partner360/entities/keyInitiatives'

import { IListStrategicPath } from '../models'

export const EMPTY_LIST_VALUE = '__ki_list_not_specified__'
export const splitListValues = (value?: string | null): string[] => [
  ...new Set(
    value
      ?.split(',')
      .map((part) => part.trim())
      .filter(Boolean) ?? []
  ),
]

/** Resolve per initiative year/offering, not the first same-named objective in a multi-year settings payload. */
export const getListStrategicPath = (
  initiative: Pick<
    TKeyInitiative,
    'Development' | 'KeyObjectiveCategory' | 'FYear' | 'ProgramType'
  >,
  objectives: IKeyObjective[]
): IListStrategicPath => {
  const development = initiative.Development?.trim()
  const storedRoot = initiative.KeyObjectiveCategory?.trim()
  if (!development)
    return { root: storedRoot || EMPTY_LIST_VALUE, path: storedRoot ? [storedRoot] : [] }
  const scoped = objectives.filter(
    (objective) => objective.fYear == null || objective.fYear === initiative.FYear
  )
  const offering = initiative.ProgramType?.trim()
  const findObjective = (key: string) => {
    const candidates = scoped.filter((objective) => objective.key === key)
    return (
      candidates.find(
        (objective) => offering && splitListValues(objective.offerings).includes(offering)
      ) ??
      candidates.find((objective) => objective.isOfferingIndependent === 1) ??
      candidates[0]
    )
  }
  const path: string[] = []
  const visited = new Set<string>()
  let key: string | undefined = development
  while (key && !visited.has(key)) {
    visited.add(key)
    path.unshift(key)
    key = findObjective(key)?.parent?.trim() || undefined
  }
  // Older/imported initiatives can have a category whose dictionary entry is no longer supplied.
  if (path.length === 1 && storedRoot && storedRoot !== development) path.unshift(storedRoot)
  return { root: path[0], path }
}

export const getListBusinessPriorities = (
  value: string | null | undefined,
  priorities: IKIBusinessPriority[]
) => {
  const names = new Map(priorities.map((priority) => [priority.uid.toLowerCase(), priority.name]))
  return splitListValues(value).map((id) => ({ id, name: names.get(id.toLowerCase()) ?? id }))
}

export const getListInitiativeKey = (
  initiative: Pick<TKeyInitiative, 'PartnerId' | 'FYear' | 'EntryId'>
) => JSON.stringify([initiative.PartnerId, initiative.FYear, initiative.EntryId])
